import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Input, message, Select } from "antd";
import style from "./index.module.scss";
import FieldWithError from "../../../../../../../components/FieldWithError";
import Form, { useForm, useWatch } from "rc-field-form";
import {
    FlagAtom,
    ShowConfirmAtom,
    StepOneFormDataAtom,
    StepOneStateAtom,
    TouchedAtom
} from "../../../../store";
import moment, { Moment } from "moment";
import {
    activityBaseInfoItem,
    getGroupList,
    GroupType,
    PublishStatus,
    saveGroupActivityBaseInfo
} from "service/group";
import { CourseContext } from "../../../../../Store";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage } from "service/service";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import Footer from "../Footer";
import Confirm from "../Confirm";
import { RuleObject, StoreValue } from "rc-field-form/es/interface";
import useRequestFn from "utils/useRequestFn";
import SubmitError from "components/SubmitError";
import useRequest from "../../../../../../../utils/useRequest";
import LabelRow from "../LabelRow";
import useModalHandler from "../../../../../../../utils/v3/useModalHandler";
import DoRandomModal from "./Components/DoRandomModal";
import ShowRandomModal from "../../../ShowRandomModal";

interface StepOneProps {
    item?: activityBaseInfoItem; //当前项
    onChangeStep: (val: number) => void; //切换上一步、下一步
    onOk: () => void; //确认
    onCancel: () => void; //取消
    handleClose: () => void; //关闭
    setActivityId: (val: number) => void; //小组id
}

interface formDataProps {
    activityName: string; //活动名称
    deadlineStr: Moment; //截止时间
    groupIds: number[]; //参与小组
    groupCount: number;
    groupName: string;
    groupType: GroupType;
    randomGroupKey?: string;
    signInId?: number;
}

export default function StepOne({
    item,
    onCancel,
    onChangeStep,
    onOk,
    handleClose,
    setActivityId
}: StepOneProps) {
    const [form] = useForm<formDataProps>();

    const flag = useAtomValue(FlagAtom);
    const { Option } = Select;
    const { course } = useContext(CourseContext);
    const updateTouched = useSetAtom(TouchedAtom);
    const [stepOneFormData, setStepOneFormData] = useAtom(StepOneFormDataAtom);
    const [showConfirm, setShowConfirm] = useAtom(ShowConfirmAtom);
    const [stepOneState, setStepOneState] = useAtom(StepOneStateAtom);
    const [msg, setMsg] = useState(false);

    const [saveGroupInfo, { loading }] = useRequestFn(
        saveGroupActivityBaseInfo
    );

    useEffect(() => {
        if (!item) return;
        form.setFieldValue("randomGroupKey", item?.randomGroupKey);
        form.setFieldValue("signInId", item?.signInId);
        setActivityId(item?.id || -1);
    }, [item]);

    // 表单变更时修改touched
    function handleValuesChange() {
        updateTouched(form.isFieldsTouched());
    }

    async function onFinish(data: formDataProps) {
        try {
            const baseParams = {
                courseNo: course.courseNo,
                courseSeq: course.courseSeq,
                weektimeId: course.weekTimeId
            };
            const params = {
                id: flag === "edit" ? item?.id : stepOneFormData?.id,
                ...data,
                groupIds: data.groupIds?.join(","),
                deadlineStr: moment(data.deadlineStr).format(
                    "YYYY-MM-DD HH:mm:ss"
                ),
                ...baseParams
            };
            const datas = await saveGroupInfo(params);
            await onOk();
            setActivityId(datas.id);
            setStepOneFormData(datas);
            onReset();
            msg && message.success("活动保存成功");
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="活动保存失败" content={errorMessage(e)} />,
                { width: 300 }
            );
        }
    }

    //保存
    const onSave = () => {
        form.submit();
        setMsg(true); //只有点击保存才提示"保存成功"
    };

    //二次弹窗，确认保存
    const onOkByModal = () => {
        form.submit();
    };

    //二次弹窗，不保存
    const onClose = () => {
        onReset();

        if (stepOneState === "current") {
            return;
        }

        if (["next", "nextTwoStep"].includes(stepOneState)) {
            onChangeStep(stepOneState === "next" ? 1 : 2);
            return;
        }
        onCancel();
    };

    //点击下一步
    const handleStep = () => {
        form.submit();
        setStepOneState("next");
    };

    //重置
    const onReset = () => {
        setShowConfirm(false);
        updateTouched(false);
        stepOneState === "close" && onCancel();
        stepOneState === "next" && onChangeStep(1);
        stepOneState === "nextTwoStep" && onChangeStep(2);
        setMsg(false);
        setStepOneState("current");
    };

    //日期选择禁选当前日期之前的日期
    function disabledDate(current: any) {
        return current && current < moment().subtract(1, "days").endOf("day");
    }

    // 校验日期
    function optionsValidator(
        rule: RuleObject,
        value: StoreValue,
        callback: (error?: string) => void
    ) {
        if (value === null) return callback("请选择截止时间");
        if (
            item?.publishStatus === "1" &&
            value.isBefore(moment(item?.deadlineStr))
        )
            return callback("调整活动截止时间，只能延长无法提前结束小组活动");
        return callback();
    }

    const [baseGroup, { loading: haveGroupLoading }] = useRequest(
        () =>
            getGroupList({
                courseNo: course.courseNo,
                courseSeq: course.courseSeq,
                weektimeId: course.weekTimeId,
                groupType: GroupType.base
            }),
        {},
        [course]
    );

    const typeOptions = useMemo(() => {
        const arr = [
            {
                label: "随机分组",
                value: GroupType.random
            }
        ];
        if (baseGroup) {
            arr.push({
                label: "固定分组",
                value: GroupType.base
            });
        }
        return arr;
    }, [baseGroup]);

    const groupType = useWatch("groupType", form);
    const randomGroupKey = useWatch("randomGroupKey", form);
    const signInId = useWatch("signInId", form);

    const defaultGroupId = useMemo(() => {
        const allGroupIds = baseGroup?.map(item => item.id.toString()) || [];
        if (flag === "edit") {
            return item?.groupIds?.split(",");
        } else {
            if (stepOneFormData?.groupIds) {
                return stepOneFormData.groupIds?.split(",");
            } else {
                return allGroupIds;
            }
        }
    }, [item?.groupIds, stepOneFormData?.groupIds, baseGroup]);

    const doRandomModal = useModalHandler({
        component: DoRandomModal
    });

    const showRandomModal = useModalHandler({
        component: ShowRandomModal
    });

    function doRandomOk(randomGroupKey: string, signInId: number) {
        form.setFieldValue("randomGroupKey", randomGroupKey);
        form.setFieldValue("signInId", signInId);
        doRandomModal.close();
    }

    return (
        <div className={style.root}>
            <Form
                form={form}
                onFinish={onFinish}
                onValuesChange={handleValuesChange}>
                <Form.Field
                    name="randomGroupKey"
                    initialValue={
                        flag === "edit"
                            ? item?.randomGroupKey
                            : stepOneFormData?.randomGroupKey
                            ? stepOneFormData.randomGroupKey
                            : ""
                    }
                />
                <Form.Field
                    name="signInId"
                    initialValue={
                        flag === "edit"
                            ? item?.signInId
                            : stepOneFormData?.signInId
                            ? stepOneFormData.signInId
                            : ""
                    }
                />
                <LabelRow text="活动名称">
                    <Form.Field
                        name="activityName"
                        initialValue={
                            flag === "edit"
                                ? item?.activityName
                                : stepOneFormData?.activityName
                                ? stepOneFormData.activityName
                                : ""
                        }>
                        <Input maxLength={200} placeholder="请输入活动名称" />
                    </Form.Field>
                </LabelRow>
                <LabelRow text="截止时间" required>
                    <FieldWithError
                        name="deadlineStr"
                        initialValue={
                            flag === "edit" && item?.deadlineStr
                                ? moment(
                                      item.deadlineStr,
                                      "YYYY-MM-DD HH:mm:ss"
                                  )
                                : stepOneFormData
                                ? moment(
                                      stepOneFormData.deadlineStr,
                                      "YYYY-MM-DD HH:mm:ss"
                                  )
                                : null
                        }
                        rules={[
                            {
                                validator: optionsValidator
                            }
                        ]}>
                        <DatePicker
                            showTime
                            placeholder="请选择截止时间"
                            format="YYYY-MM-DD HH:mm"
                            disabledDate={disabledDate}
                        />
                    </FieldWithError>
                </LabelRow>

                <LabelRow text="分组方式" required>
                    {typeOptions && (
                        <FieldWithError
                            name="groupType"
                            initialValue={
                                flag === "edit"
                                    ? item?.groupType
                                    : stepOneFormData?.groupType
                                    ? stepOneFormData.groupType
                                    : GroupType.base
                            }>
                            <Select loading={haveGroupLoading}>
                                {typeOptions?.map(item => {
                                    return (
                                        <Select.Option
                                            value={item.value}
                                            key={item.value}>
                                            {item.label}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </FieldWithError>
                    )}
                </LabelRow>
                {groupType === GroupType.random && (
                    <>
                        <LabelRow text="当前随机分组">
                            <>
                                {randomGroupKey && (
                                    <span>
                                        已分组
                                        <Button
                                            type="link"
                                            onClick={() =>
                                                showRandomModal.open({
                                                    randomGroupKey,
                                                    signInId: signInId!
                                                })
                                            }>
                                            点击预览
                                        </Button>
                                    </span>
                                )}
                                {!randomGroupKey && (
                                    <span>
                                        当前不存在有效的随机分组
                                        请点击下方【点击生成按钮】后继续
                                    </span>
                                )}
                            </>
                        </LabelRow>
                        <LabelRow text="生成随机分组">
                            <Button
                                onClick={() =>
                                    doRandomModal.open({
                                        onSuccess: doRandomOk
                                    })
                                }>
                                点击生成
                            </Button>
                        </LabelRow>
                    </>
                )}

                {groupType === GroupType.base && (
                    <LabelRow text="参与小组" required>
                        <FieldWithError
                            name="groupIds"
                            preserve={false}
                            initialValue={defaultGroupId}
                            rules={[
                                {
                                    required: true,
                                    message: "请选择参与小组"
                                }
                            ]}>
                            <Select
                                getPopupContainer={triggerNode =>
                                    triggerNode.parentElement || document.body
                                }
                                disabled={
                                    item?.publishStatus ===
                                    PublishStatus.Waiting
                                }
                                allowClear
                                mode="multiple"
                                className={style.select}
                                placeholder="请选择参与小组"
                                showArrow>
                                {baseGroup?.map(item => (
                                    <Option
                                        value={item.id.toString()}
                                        key={item.id}>
                                        {item.groupName}
                                    </Option>
                                ))}
                            </Select>
                        </FieldWithError>
                    </LabelRow>
                )}
            </Form>

            {showConfirm && <Confirm onCancel={onClose} onOk={onOkByModal} />}

            <Footer>
                <Button onClick={handleClose}>取消</Button>
                <Button
                    type="primary"
                    ghost
                    onClick={onSave}
                    loading={loading}
                    disabled={
                        groupType === GroupType.random && !randomGroupKey
                    }>
                    保存
                </Button>
                <Button
                    type="primary"
                    onClick={handleStep}
                    disabled={
                        groupType === GroupType.random && !randomGroupKey
                    }>
                    下一步
                </Button>
            </Footer>

            {doRandomModal.render()}
            {showRandomModal.render()}
        </div>
    );
}
